import * as React from 'react'

// Customer notes migrated from Momence store their bodies as HTML fragments
// (<h2>PROGRAM</h2><p>Perm Pressure : -20</p>…). Notes written through chat are
// plain text. This renders both: HTML is parsed with DOMParser and rebuilt as
// React elements from a tag allowlist — never dangerouslySetInnerHTML, so a
// hostile note body cannot script — and anything else renders as plain text
// with newlines preserved.

const TEXT = 'text-[13.5px] leading-[19px] text-foreground'
const HEADING = 'mt-2 text-[12px] font-bold tracking-[0.3px] text-muted-foreground uppercase first:mt-0'

// Tags rendered structurally. Everything else (span, div, a, font, …) is
// unwrapped — its children render, the tag itself contributes nothing.
const BLOCK_TAGS = new Set(['p', 'ul', 'ol', 'li', 'blockquote'])
const HEADING_TAGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6'])
// Never render, never descend into.
const DROP_TAGS = new Set(['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'title', 'svg', 'img', 'form', 'input', 'button', 'textarea', 'select'])

export function looksLikeHtml(text: string): boolean {
  return /<([a-z][a-z0-9]*)(\s[^<>]*)?>/i.test(text)
}

function renderChildren(node: Node, keyBase: string): React.ReactNode[] {
  const out: React.ReactNode[] = []
  node.childNodes.forEach((child, i) => {
    const rendered = renderNode(child, `${keyBase}.${i}`)
    if (rendered !== null) out.push(rendered)
  })
  return out
}

function renderNode(node: Node, key: string): React.ReactNode {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent
  if (node.nodeType !== Node.ELEMENT_NODE) return null

  const tag = (node as Element).tagName.toLowerCase()
  if (DROP_TAGS.has(tag)) return null

  const children = renderChildren(node, key)

  if (HEADING_TAGS.has(tag)) return <h4 key={key} className={HEADING}>{children}</h4>
  if (tag === 'br') return <br key={key} />
  if (tag === 'ul') return <ul key={key} className={`${TEXT} list-disc pl-5`}>{children}</ul>
  if (tag === 'ol') return <ol key={key} className={`${TEXT} list-decimal pl-5`}>{children}</ol>
  if (tag === 'li') return <li key={key}>{children}</li>
  if (tag === 'blockquote') return <blockquote key={key} className={`${TEXT} border-l-2 border-border pl-2`}>{children}</blockquote>
  if (BLOCK_TAGS.has(tag)) {
    // An empty <p> (or <p><br></p>, the editor's blank line) collapses to nothing —
    // the gap between blocks already separates sections.
    if (children.every((c) => c === '' || (typeof c === 'string' && c.trim() === '') || (React.isValidElement(c) && c.type === 'br'))) return null
    return <p key={key} className={TEXT}>{children}</p>
  }
  if (tag === 'strong' || tag === 'b') return <strong key={key}>{children}</strong>
  if (tag === 'em' || tag === 'i') return <em key={key}>{children}</em>
  if (tag === 'u') return <u key={key}>{children}</u>
  if (tag === 's') return <s key={key}>{children}</s>

  // Unknown tag: unwrap.
  return <React.Fragment key={key}>{children}</React.Fragment>
}

/**
 * A note as plain text, for previews that cannot hold markup (a one-line hint, a
 * clamped card). An HTML note's blocks become lines, so "<p>Note:</p><p>Ishita…"
 * reads "Note:\nIshita…" instead of printing its tags.
 */
export function notePlainText(text: string): string {
  if (!looksLikeHtml(text)) return text
  const lines: string[] = []
  let line = ''
  const flush = () => {
    if (line.trim()) lines.push(line)
    line = ''
  }
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      line += node.textContent ?? ''
      return
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return
    const tag = (node as Element).tagName.toLowerCase()
    if (DROP_TAGS.has(tag)) return
    if (tag === 'br') return flush()
    const block = BLOCK_TAGS.has(tag) || HEADING_TAGS.has(tag)
    if (block) flush()
    node.childNodes.forEach(walk)
    if (block) flush()
  }
  new DOMParser().parseFromString(text, 'text/html').body.childNodes.forEach(walk)
  flush()
  return lines.map((l) => l.replace(/\s+/g, ' ').trim()).join('\n')
}

export function NoteBody({ text }: { text: string }) {
  if (!looksLikeHtml(text)) {
    return <p className={`${TEXT} whitespace-pre-wrap`}>{text}</p>
  }
  const body = new DOMParser().parseFromString(text, 'text/html').body
  return <div className="flex flex-col gap-1">{renderChildren(body, 'n')}</div>
}
